import { Copy } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { getSignInToken, type OrgMember } from '../models/user'

interface SignInTokenDialogProps {
  member: OrgMember
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SignInTokenDialog({ member, open, onOpenChange }: SignInTokenDialogProps) {
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function fetchToken() {
    setLoading(true)
    setError(null)
    try {
      const result = await getSignInToken(member.id)
      setToken(result.token)
    } catch {
      setError('Failed to create sign-in token. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && !token && !loading) {
      fetchToken()
    }
    if (!open) {
      setToken(null)
      setError(null)
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // The target's id lets a same-account link no-op instead of prompting a switch; the
  // label names them on the switch screen.
  const targetLabel = [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email
  const signInUrl = token
    ? `${window.location.origin}/login-token?token=${encodeURIComponent(token)}&source=staff&label=${encodeURIComponent(targetLabel)}&uid=${encodeURIComponent(member.id)}`
    : ''

  function handleCopy() {
    navigator.clipboard.writeText(signInUrl)
    toast.success('Link copied to clipboard')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Login Token</DialogTitle>
          <DialogDescription>
            One-time login link for{' '}
            <strong>
              {member.firstName} {member.lastName}
            </strong>{' '}
            ({member.email}).
            <div className="mt-2" />
            This link expires in 24 hours and can only be used once.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-4">
            <Spinner className="size-6" />
            <span className="ml-2 text-sm text-gray-500">Generating login link...</span>
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        {token && (
          <div className="flex items-center gap-2">
            <Input value={signInUrl} disabled className="flex-1 font-mono text-xs" />
            <Button variant="outline" size="icon" onClick={handleCopy} aria-label="Copy link">
              <Copy className="size-4" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
